import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { PermissionService } from '../services/permission.service';
import { AuthService } from '../services/auth.service';

/**
 * Guard to protect routes based on per-marina roles.
 * 
 * Checks if user has required role in the current marina (or specified marina).
 * Uses cached Init API data (from UserMarinaRole table), NOT JWT claims.
 * 
 * System admin bypass: System admins can access all routes regardless of roles.
 * 
 * Usage:
 * { 
 *   path: 'admin', 
 *   component: AdminComponent, 
 *   canActivate: [RoleGuard], 
 *   data: { 
 *     roles: ['ADMIN', 'DOCK_MASTER'],  // Required roles (any of these)
 *     marinaId: 'optional-marina-id'    // Optional: check role in specific marina (defaults to current marina)
 *   } 
 * }
 */
@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate {
  constructor(
    private permissionService: PermissionService,
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Check if user is authenticated first
    const isAuthenticated = this.authService.isAuthenticated();
    
    // If not authenticated, check if access token is expired but refresh token is still valid
    // If so, wait for init() to complete before checking roles
    if (!isAuthenticated) {
      const accessTokenExpiresAt = this.authService.getAccessTokenExpiresAt();
      const refreshTokenExpiresAt = this.authService.getRefreshTokenExpiresAt();
      const now = Date.now();
      
      if (accessTokenExpiresAt && refreshTokenExpiresAt) {
        const accessTokenExpired = accessTokenExpiresAt.getTime() <= now;
        const refreshTokenValid = refreshTokenExpiresAt.getTime() > now;
        
        if (refreshTokenValid) {
          // We have valid tokens but initData is missing (new tab scenario)
          // Trigger init() to restore state, then wait for it to complete before checking roles
          console.log('[RoleGuard] Valid refresh token but initData missing (new tab) - triggering init()...');
          
          // Check if initData is already available (race condition - it was set between checks)
          const currentInitData = this.authService.initDataValue;
          if (currentInitData) {
            // initData is already available, continue with role check below
            console.log('[RoleGuard] ✅ initData became available, continuing with role check');
          } else {
            // Trigger init() and wait for it to complete before checking roles
            return this.authService.init().pipe(
              map((initResponse) => {
                console.log('[RoleGuard] ✅ init() completed, continuing with role check');
                // After init() completes, continue with role check below
                // We'll fall through to the role checking logic
                return this.checkRoles(route, state);
              }),
              catchError((error) => {
                console.error('[RoleGuard] ❌ init() failed:', error);
                // If init() fails, redirect to login
                this.router.navigate(['/auth/login'], {
                  queryParams: { returnUrl: state.url },
                });
                return of(false);
              })
            );
          }
        } else {
          // Both tokens expired or no tokens - redirect to login
          console.log('[RoleGuard] Not authenticated and no valid refresh token, redirecting to login');
          this.router.navigate(['/auth/login'], {
            queryParams: { returnUrl: state.url },
          });
          return false;
        }
      } else {
        // No token expiration info - redirect to login
        console.log('[RoleGuard] Not authenticated and no token expiration info, redirecting to login');
        this.router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url },
        });
        return false;
      }
    }
    
    // Continue with role check (either initData is available or init() completed)
    return this.checkRoles(route, state);
  }

  /**
   * Check if user has required roles
   * This method is called after init() completes to ensure initData is available
   */
  private checkRoles(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    // System admin can access all routes
    if (this.permissionService.isSystemAdmin()) {
      return true;
    }

    // Get required roles from route data
    const requiredRoles = route.data['roles'] as string[] | undefined;
    if (!requiredRoles || requiredRoles.length === 0) {
      // No roles specified, allow access if authenticated
      return true;
    }

    // Get marina ID from route data or use current marina
    const marinaId =
      (route.data['marinaId'] as string) || this.authService.getCurrentMarinaId();

    // Check if initData is available (required for role checks)
    const initData = this.authService.initDataValue;
    if (!initData) {
      // initData not available - this shouldn't happen if we waited for init()
      // But just in case, allow access temporarily
      console.warn('[RoleGuard] ⚠️ initData still not available in checkRoles() - temporarily allowing access');
      return true;
    }

    if (!marinaId) {
      // No marina selected, deny access
      console.log('[RoleGuard] No marina selected, redirecting to unauthorized');
      this.router.navigate(['/unauthorized']);
      return false;
    }

    // Check if user has any of the required roles in the specified marina
    // requiredRoles is guaranteed to be defined and non-empty due to check above
    const hasAccess = this.permissionService.hasAnyMarinaRole(
      marinaId,
      ...(requiredRoles as string[])
    );

    if (!hasAccess) {
      // User doesn't have required role, redirect to unauthorized
      console.log('[RoleGuard] User does not have required role, redirecting to unauthorized');
      this.router.navigate(['/unauthorized']);
      return false;
    }

    return true;
  }
}
